import { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2,
  ChevronLeft,
  LockKeyhole,
  MessageSquareWarning,
  MonitorCog,
  RotateCcw,
  Save,
  ShieldAlert,
  X,
} from 'lucide-react';
import { Button } from '@kop/ui';

import { HazardReportForm } from '../hazard-report/hazard-report-form';
import {
  clearFeedbackDeviceDraft,
  readFeedbackDeviceDraft,
  saveFeedbackDeviceDraft,
  type FeedbackDeviceDraft,
} from './feedback-draft';
import {
  buildFeedbackSubmission,
  initialFeedbackDraft,
  type FeedbackCategory,
  type FeedbackDraft,
} from './model';
import {
  createFeedbackRepository,
  uploadFeedbackScreenshot,
  type FeedbackRecord,
  type FeedbackRepository,
  type FeedbackScope,
} from './repository';

const categories: Array<[FeedbackCategory, string]> = [
  ['usability', '사용이 불편해요'],
  ['performance', '느리거나 멈춰요'],
  ['error', '오류가 발생해요'],
  ['compatibility', '기기에서 달라요'],
  ['data-error', '내용이 맞지 않아요'],
  ['feature-request', '기능을 제안해요'],
];

type Props = {
  identity: { id: string; name: string };
  scope: FeedbackScope;
  route: string;
  initialView?: 'report' | 'mine';
  onClose(): void;
};

/**
 * One control, two destinations. The first step decides which: 시스템 개선 · 건의 keeps the
 * existing feedback flow exactly as it was, 현장 위험요인 제안 opens the 안전신문고 form. The
 * second is deliberately the shortest path in the product - a person who has just seen a hazard
 * chooses, describes and submits without hunting through the menu.
 */
type FeedbackChannel = 'product' | 'hazard';

const statusLabel: Record<string, string> = {
  received: '접수됨',
  'awaiting-approval': '승인 검토',
  analyzing: '원인 분석',
  'needs-information': '정보 요청',
  reproducing: '격리 재현',
  deferred: '보류',
  rejected: '종결',
  'merged-duplicate': '중복 연결',
};

const focusable =
  'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export function FeedbackDialog({ identity, scope, route, initialView = 'report', onClose }: Props) {
  const panelRef = useRef<HTMLElement>(null);
  const [draft, setDraft] = useState<FeedbackDraft>({ ...initialFeedbackDraft });
  const [savedDraft, setSavedDraft] = useState<FeedbackDeviceDraft | null>(() =>
    readFeedbackDeviceDraft(window.localStorage, identity.id, scope),
  );
  const [channel, setChannel] = useState<FeedbackChannel | null>(
    initialView === 'mine' ? 'product' : null,
  );
  const [view, setView] = useState<'report' | 'mine'>(initialView);
  const [repository, setRepository] = useState<FeedbackRepository | null>(null);
  const [own, setOwn] = useState<FeedbackRecord[]>([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [screenshot, setScreenshot] = useState<File | null>(null);

  useEffect(() => {
    let active = true;
    void createFeedbackRepository({
      storage: window.localStorage,
      identityId: identity.id,
      displayName: identity.name,
    })
      .then(async (created) => {
        const records = await created.listOwn(scope);
        if (!active) return;
        setRepository(created);
        setOwn(records);
      })
      .catch((error: unknown) => {
        if (active)
          setMessage(error instanceof Error ? error.message : '내 신고를 불러오지 못했습니다.');
      });
    return () => {
      active = false;
    };
  }, [identity.id, identity.name, scope]);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.querySelector<HTMLElement>(focusable)?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!busy) onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const items = [...panelRef.current.querySelectorAll<HTMLElement>(focusable)].filter(
        (item) => item.offsetParent !== null,
      );
      if (!items.length) return;
      const first = items[0]!;
      const last = items.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus();
    };
  }, [busy, onClose]);

  const saveDraft = () => {
    const saved = saveFeedbackDeviceDraft(window.localStorage, {
      ownerId: identity.id,
      scope,
      draft,
      route,
      now: new Date().toISOString(),
    });
    setSavedDraft(saved);
    setMessage('이 기기에 임시저장했습니다. 서버 접수로 처리되지 않습니다.');
  };

  const submit = async () => {
    if (!repository) return;
    setBusy(true);
    setMessage('');
    try {
      const now = new Date().toISOString();
      const submission = buildFeedbackSubmission(draft, {
        route,
        now,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        online: navigator.onLine,
        screenshotEvidence: draft.screenshotConsent
          ? await uploadFeedbackScreenshot({
              file: screenshot!,
              feedbackId: `feedback-${now.replace(/\D/gu, '').slice(0, 14)}`,
              scope,
            })
          : null,
      });
      await repository.submit({
        tenantId: scope.tenantId,
        feedbackId: submission.feedbackId,
        commandId: `submit-${submission.feedbackId}`,
        idempotencyKey: `submit-${identity.id}-${submission.feedbackId}`,
        scope,
        report: submission.report,
      });
      clearFeedbackDeviceDraft(window.localStorage, identity.id, scope);
      setSavedDraft(null);
      setDraft({ ...initialFeedbackDraft });
      setScreenshot(null);
      setOwn(await repository.listOwn(scope));
      setView('mine');
      setMessage('불편 신고가 접수되었습니다. 내 신고에서 처리 상태를 확인할 수 있습니다.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '접수하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const progress = draft.description.trim() ? 50 : 0;
  const reporting = channel === 'product' && view === 'report';
  const eyebrow =
    channel === null
      ? '접수할 내용 선택'
      : channel === 'hazard'
        ? '현장에서 발견한 위험'
        : view === 'report'
          ? '현재 화면에서 바로 접수'
          : '내 신고 처리 상태';

  return (
    <div
      className="feedback-dialog-backdrop"
      onMouseDown={() => {
        if (!busy) onClose();
      }}
    >
      <section
        ref={panelRef}
        className="feedback-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-dialog-title"
        aria-describedby="feedback-dialog-description"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span>{eyebrow}</span>
            <h2 id="feedback-dialog-title">신고·건의</h2>
          </div>
          <button
            className="feedback-dialog-close"
            aria-label="신고·건의 닫기"
            onClick={onClose}
            disabled={busy}
          >
            <X size={20} />
          </button>
        </header>
        <p id="feedback-dialog-description">
          {channel === null
            ? '무엇을 접수할지 먼저 골라 주세요. 고른 뒤 바로 작성할 수 있습니다.'
            : channel === 'hazard'
              ? '발견한 위치와 위험 내용을 남기면 안전관리자가 확인합니다.'
              : view === 'report'
                ? '업무를 중단하지 않고 불편한 점을 남겨 주세요. 현재 경로만 신고 맥락으로 기록합니다.'
                : '내가 접수한 신고와 현재 처리 상태만 확인할 수 있습니다.'}
        </p>

        {channel === null && (
          <div className="feedback-channel-choice" role="group" aria-label="접수할 내용">
            <button
              type="button"
              className="feedback-channel-card"
              onClick={() => {
                setChannel('product');
                setView('report');
              }}
            >
              <MonitorCog size={20} />
              <span>
                <strong>시스템 개선 · 건의</strong>
                <small>화면이나 기능이 불편할 때</small>
              </span>
            </button>
            <button
              type="button"
              className="feedback-channel-card"
              onClick={() => setChannel('hazard')}
            >
              <ShieldAlert size={20} />
              <span>
                <strong>현장 위험요인 제안</strong>
                <small>현장에서 위험을 발견했을 때</small>
              </span>
            </button>
          </div>
        )}

        {channel !== null && (
          <button
            type="button"
            className="feedback-step-back"
            onClick={() => {
              setChannel(null);
              setView('report');
            }}
          >
            <ChevronLeft size={15} /> 접수할 내용 다시 선택
          </button>
        )}

        {channel === 'hazard' && <HazardReportForm identity={identity} scope={scope} />}

        {channel === 'product' && (
          <nav className="feedback-tabs" aria-label="시스템 개선 메뉴">
            <button className={view === 'report' ? 'active' : ''} onClick={() => setView('report')}>
              시스템 개선 · 건의
            </button>
            <button className={view === 'mine' ? 'active' : ''} onClick={() => setView('mine')}>
              내 신고 <span>{own.length}</span>
            </button>
          </nav>
        )}

        {reporting && (
          <div className="feedback-draft-status" aria-label="신고 초안 상태">
            <span>
              <strong>작성 중</strong> · 기기 임시 상태
            </span>
            <span>
              작성자 {identity.name} · {progress}%
            </span>
            <span>다음 작업: 내용 입력 후 접수</span>
          </div>
        )}

        {reporting && (
          <div className="feedback-draft-actions">
            <button
              onClick={() => {
                setDraft({ ...initialFeedbackDraft });
                setMessage('빈 새 신고를 시작했습니다. 저장된 임시글은 유지됩니다.');
              }}
            >
              <RotateCcw size={15} /> 새 신고
            </button>
            {savedDraft && (
              <button
                onClick={() => {
                  setDraft(savedDraft.draft);
                  setMessage(
                    `${new Date(savedDraft.lastSavedAt).toLocaleString('ko-KR')} 임시글을 열었습니다.`,
                  );
                }}
              >
                내 임시저장 이어쓰기
              </button>
            )}
          </div>
        )}

        {reporting && (
          <div className="feedback-dialog-categories" role="group" aria-label="불편 신고 분류">
            {categories.map(([value, label]) => (
              <button
                key={value}
                className={draft.category === value ? 'selected' : ''}
                aria-pressed={draft.category === value}
                onClick={() => setDraft((current) => ({ ...current, category: value }))}
              >
                {draft.category === value && <CheckCircle2 size={14} />}
                {label}
              </button>
            ))}
          </div>
        )}

        {reporting && (
          <label className="feedback-dialog-description-field">
            불편한 점
            <textarea
              value={draft.description}
              onChange={(event) =>
                setDraft((current) => ({ ...current, description: event.target.value }))
              }
              placeholder="어떤 화면에서 무엇을 하려 했고, 실제로 어떤 일이 있었는지 적어 주세요."
            />
          </label>
        )}
        {reporting && (
          <label className="feedback-dialog-consent">
            <input
              type="checkbox"
              checked={draft.diagnosticsConsent}
              onChange={(event) =>
                setDraft((current) => ({ ...current, diagnosticsConsent: event.target.checked }))
              }
            />
            <span>
              <strong>최소 기기 정보를 함께 보냅니다</strong>
              <small>
                화면 크기, 온라인 상태, 현재 경로만 포함하며 생체·계정 비밀정보는 수집하지 않습니다.
              </small>
            </span>
          </label>
        )}
        {reporting && (
          <label className="feedback-dialog-consent">
            <input
              type="checkbox"
              checked={draft.screenshotConsent}
              onChange={(event) => {
                setDraft((current) => ({
                  ...current,
                  screenshotConsent: event.target.checked,
                  screenshotName: event.target.checked ? current.screenshotName : '',
                }));
                if (!event.target.checked) setScreenshot(null);
              }}
            />
            <span>
              <strong>현재 화면 캡처를 증거로 첨부합니다</strong>
              <small>파일과 첨부 권한을 확인한 뒤 접수됩니다.</small>
            </span>
          </label>
        )}
        {reporting && draft.screenshotConsent && (
          <label className="feedback-dialog-description-field">
            화면 캡처
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                setScreenshot(file);
                setDraft((current) => ({ ...current, screenshotName: file?.name ?? '' }));
              }}
            />
            <small>{screenshot?.name ?? '제출 전에 캡처 파일을 선택해 주세요.'}</small>
          </label>
        )}

        {channel === 'product' && view === 'mine' && (
          <section className="feedback-history" aria-label="내 신고 처리 상태">
            {own.length === 0 ? (
              <div className="empty-state">
                <MessageSquareWarning size={25} />
                <h3>접수한 불편 신고가 없습니다.</h3>
                <p>불편 신고를 남기면 이곳에서 진행 상태를 확인할 수 있습니다.</p>
              </div>
            ) : (
              own.map((item) => (
                <article key={item.feedbackId}>
                  <span className="feedback-status-dot" />
                  <div>
                    <strong>{statusLabel[item.status] ?? item.status}</strong>
                    <p>{item.report.description}</p>
                    <small>{new Date(item.updatedAt).toLocaleString('ko-KR')}</small>
                  </div>
                </article>
              ))
            )}
          </section>
        )}

        {channel !== 'hazard' && message && (
          <p className="feedback-dialog-message" role="status">
            {message}
          </p>
        )}

        {reporting && (
          <footer>
            <button className="feedback-save-draft" onClick={saveDraft} disabled={busy}>
              <Save size={16} /> 기기에 임시저장
            </button>
            <Button
              disabled={busy || !repository || (draft.screenshotConsent && !screenshot)}
              onClick={() => void submit()}
            >
              <MessageSquareWarning size={16} /> 접수
            </Button>
          </footer>
        )}
        {channel !== 'hazard' && (
          <small className="feedback-dialog-trust">
            <LockKeyhole size={14} />
            {channel === null
              ? '접수가 완료되면 처리 이력을 확인할 수 있습니다.'
              : view === 'report'
                ? '임시저장은 접수 완료가 아니며, 접수 성공 후에만 요청 이력이 생성됩니다.'
                : '다른 사용자의 신고와 관리자용 분류 정보는 표시되지 않습니다.'}
          </small>
        )}
      </section>
    </div>
  );
}
